import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '../firebase';
import TopNavigation from '../components/TopNavigation';
import LeftNavSidebar from '../components/LeftNavSidebar';
import RightWidgetsSidebar from '../components/RightWidgetsSidebar';
import ImageWithFallback from '../components/ImageWithFallback';
import { validateContent, filterProfanity } from '../utils/contentModeration';
import GroupComments from '../components/GroupComments';
import { createGroupPost, createGroupReply, createGroupThread, deleteGroup, deleteGroupPost, deleteGroupThread, getGroupById, getGroupTeam, getUserMe, GroupRole, joinGroup, leaveGroup, listGroupPosts, listGroupReplies, listGroupThreads, toggleGroupPostLike, toggleGroupThreadLike, updateGroupThread } from '../utils/secureFunctions';
import GroupManagePanel from '../components/GroupManagePanel';
import UserLink from '../components/UserLink';
import AdSlot from '../components/AdSlot';
import { appConfirm } from '../utils/dialog';

interface Group {
  id: string;
  name: string;
  description: string;
  category: string;
  isPrivate: boolean;
  coverImage?: string;
  adminId: string;
  adminName: string;
  moderators?: string[];
  members: string[];
  memberCount: number;
  createdAt: any;
}

interface GroupPost {
  id: string;
  groupId: string;
  userId: string;
  userName: string;
  userPhoto: string;
  content: string;
  imageURL?: string;
  likes: string[];
  comments: number;
  createdAt: any;
}

interface GroupThread { id: string; groupId: string; userId: string; userName: string; userPhoto: string; title: string; body: string; repliesCount: number; createdAt: any; pinned?: boolean; locked?: boolean; likes: string[]; }
interface GroupReply { id: string; threadId: string; groupId: string; userId: string; userName: string; userPhoto: string; body: string; createdAt: any; likes: string[]; parentId?: string | null; }

const GroupDetail: React.FC = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  // Responsive breakpoint should be initialized unconditionally before any early returns
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [group, setGroup] = useState<Group | null>(null);
  const [posts, setPosts] = useState<GroupPost[]>([]);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [threads, setThreads] = useState<GroupThread[]>([]);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadBody, setNewThreadBody] = useState('');
  const [postingThread, setPostingThread] = useState(false);
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [repliesByThread, setRepliesByThread] = useState<Record<string, GroupReply[]>>({});
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [loadingReplies, setLoadingReplies] = useState<Record<string, boolean>>({});
  const [highlightThreadId, setHighlightThreadId] = useState<string | null>(null);
  const [openCommentsByPost, setOpenCommentsByPost] = useState<Record<string, boolean>>({});
  const [replyingToReply, setReplyingToReply] = useState<Record<string, boolean>>({});
  const [childReplyText, setChildReplyText] = useState<Record<string, string>>({});
  // UI tabs
  const [activeTab, setActiveTab] = useState<'posts' | 'discussions'>('posts');
  const [postView, setPostView] = useState<'new' | 'active' | 'mine'>('new');

  // Dev helper: show index creation hint if the backend returns a missing-index link
  function showIndexHint(err: any, context: string) {
    try {
      if (process.env.NODE_ENV === 'production') return;
      const msg = String(err?.message || '');
      const m = msg.match(/https?:\/\/\S+/);
      if (m && m[0].includes('createIndex')) {
        alert(`A database index may be required (${context}).\nOpen to create: ${m[0]}`);
      }
    } catch {}
  }

  const uid = auth.currentUser?.uid || '';
  const [myRole, setMyRole] = useState<GroupRole | null>(null);
  const [isSuper, setIsSuper] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const canManage = myRole === 'owner' || myRole === 'admin' || myRole === 'moderator'
    || (!!group && (group.adminId === uid || (group.moderators || []).includes(uid)));
  const isMember = !!group && ((group.members || []).includes(uid) || !!myRole);

  useEffect(() => {
    if (!groupId) return;
    getGroupTeam(groupId).then((r) => setMyRole(r.myRole)).catch(() => setMyRole(null));
  }, [groupId]);

  useEffect(() => {
    if (!auth.currentUser) return;
    getUserMe()
      .then((me) => setIsSuper((me.profile as { role?: string } | undefined)?.role === 'super_admin'))
      .catch(() => {});
  }, []);

  async function handleDeleteGroup() {
    if (!group || !await appConfirm(`Delete the group "${group.name}" with all its posts and discussions? This cannot be undone.`)) return;
    try {
      await deleteGroup(group.id);
      navigate('/groups');
    } catch (e: any) { alert(e?.message || 'Failed to delete group'); }
  }

  useEffect(() => {
    if (!groupId) return;
    let active = true;
    const load = async () => {
      try {
        const res = await getGroupById(groupId);
        if (!active) return;
        const data = res.group as any;
        if (data) {
          setGroup({ id: data.id, ...data, members: Array.isArray(data.members) ? data.members : [] });
        }
      } catch (e) {
        console.warn('Failed to load group', e);
      }
    };
    load();
    const interval = setInterval(load, 20000);
    return () => { active = false; clearInterval(interval); };
  }, [groupId]);

  // Members modal removed; dedicated members page will handle loading

  useEffect(() => {
    if (!groupId) return;
    let active = true;
    const load = async () => {
      try {
        const res = await listGroupPosts(groupId);
        if (!active) return;
        const arr: GroupPost[] = (res.posts || []).map((data: any) => ({
          id: data.id,
          ...data,
          likes: Array.isArray(data.likes) ? data.likes : [],
        }));
        setPosts(arr);
      } catch (e) {
        showIndexHint(e, 'Group Posts');
      }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => { active = false; clearInterval(interval); };
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    let active = true;
    const load = async () => {
      try {
        const res = await listGroupThreads(groupId, 'new');
        if (!active) return;
        const arr: GroupThread[] = (res.threads || []).map((data: any) => ({
          id: data.id,
          ...data,
          likes: Array.isArray(data.likes) ? data.likes : [],
        }));
        setThreads(arr);
      } catch (e) {
        showIndexHint(e, 'Group Threads');
      }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => { active = false; clearInterval(interval); };
  }, [groupId]);

  // Stable replies loader for use in effects and handlers
  const loadReplies = useCallback(async (threadId: string) => {
    setLoadingReplies((m) => ({ ...m, [threadId]: true }));
    try {
      const res = await listGroupReplies(threadId);
      const arr: GroupReply[] = (res.replies || []).map((data: any) => ({
        id: data.id,
        ...data,
        likes: Array.isArray(data.likes) ? data.likes : [],
        parentId: data.parentId ?? null,
      }));
      setRepliesByThread((m) => ({ ...m, [threadId]: arr }));
    } catch (e) {
      console.error('Load replies failed', e);
      showIndexHint(e, 'Thread Replies');
    } finally {
      setLoadingReplies((m) => ({ ...m, [threadId]: false }));
    }
  }, []);

  // If a threadId is provided in the URL, expand and scroll to it once threads are loaded
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('threadId');
    if (!targetId) return;
    // When threads update, see if target exists and then expand and scroll
    const exists = threads.some(t => t.id === targetId);
    if (!exists) return;
    setExpandedThreadId(targetId);
    setHighlightThreadId(targetId);
    loadReplies(targetId);
    // Scroll after DOM paints
    setTimeout(() => {
      const el = document.getElementById(`thread-${targetId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    // Clear highlight after a short delay
    const t = setTimeout(() => setHighlightThreadId(null), 2000);
    return () => clearTimeout(t);
  }, [threads, loadReplies]);

  const handleCreatePost = async () => {
    if (!group || !auth.currentUser || !newPost.trim()) return;
    const v = validateContent(newPost);
    if (!v.isValid) { alert(v.message); return; }
    setPosting(true);
    try {
      const me = await getUserMe();
      await createGroupPost(group.id, {
        content: filterProfanity(newPost),
        userName: me.profile?.name || 'Anonymous',
        userPhoto: me.profile?.photoURL || '',
      });
      setNewPost('');
    } catch (e) {
      console.error('Create group post failed', e);
      alert('Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  async function handleCreateThread() {
    if (!group || !auth.currentUser || !newThreadTitle.trim() || !newThreadBody.trim()) return;
    setPostingThread(true);
    try {
      const me = await getUserMe();
      const res = await createGroupThread({
        groupId: group.id,
        groupName: group.name,
        groupCategory: group.category,
        title: newThreadTitle.trim(),
        body: newThreadBody.trim(),
        userName: me.profile?.name || 'Anonymous',
        userPhoto: me.profile?.photoURL || '',
      });
      setNewThreadTitle('');
      setNewThreadBody('');
      setExpandedThreadId(res.thread.id);
    } catch (e) {
      console.error('Create thread failed', e);
      alert('Failed to create thread');
    } finally {
      setPostingThread(false);
    }
  }

  

  async function handlePostReply(thread: GroupThread) {
    const text = (replyText[thread.id] || '').trim();
    if (!group || !auth.currentUser || !text) return;
    setLoadingReplies((m) => ({ ...m, [thread.id]: true }));
    try {
      const me = await getUserMe();
      await createGroupReply(thread.id, {
        groupId: group.id,
        body: text,
        parentId: null,
        userName: me.profile?.name || 'Anonymous',
        userPhoto: me.profile?.photoURL || '',
      });
      setReplyText((m) => ({ ...m, [thread.id]: '' }));
      await loadReplies(thread.id);
    } catch (e) {
      console.error('Post reply failed', e);
      alert('Failed to post reply');
    } finally {
      setLoadingReplies((m) => ({ ...m, [thread.id]: false }));
    }
  }

  async function handlePostChildReply(parent: GroupReply, thread: GroupThread) {
    const text = (childReplyText[parent.id] || '').trim();
    if (!group || !auth.currentUser || !text) return;
    setLoadingReplies((m) => ({ ...m, [thread.id]: true }));
    try {
      const me = await getUserMe();
      await createGroupReply(thread.id, {
        groupId: group.id,
        body: text,
        parentId: parent.id,
        userName: me.profile?.name || 'Anonymous',
        userPhoto: me.profile?.photoURL || '',
      });
      setChildReplyText((m) => ({ ...m, [parent.id]: '' }));
      setReplyingToReply((m) => ({ ...m, [parent.id]: false }));
      await loadReplies(thread.id);
    } catch (e) {
      console.error('Post child reply failed', e);
      alert('Failed to reply');
    } finally {
      setLoadingReplies((m) => ({ ...m, [thread.id]: false }));
    }
  }

  async function handleTogglePin(t: GroupThread) {
    if (!canManage) return;
    try {
      await updateGroupThread(t.id, { pinned: !t.pinned });
      setThreads((prev) => prev.map((th) => (th.id === t.id ? { ...th, pinned: !t.pinned } : th)));
    } catch (e: any) { alert(e?.message || 'Failed to update thread'); }
  }
  async function handleToggleLock(t: GroupThread) {
    if (!canManage) return;
    try {
      await updateGroupThread(t.id, { locked: !t.locked });
      setThreads((prev) => prev.map((th) => (th.id === t.id ? { ...th, locked: !t.locked } : th)));
    } catch (e: any) { alert(e?.message || 'Failed to update thread'); }
  }
  async function handleDeleteThread(t: GroupThread) {
    if (!await appConfirm('Delete this discussion and all its replies?')) return;
    try {
      await deleteGroupThread(t.id);
      setThreads((prev) => prev.filter((th) => th.id !== t.id));
    } catch (e: any) { alert(e?.message || 'Failed to delete thread'); }
  }
  async function handleDeletePost(p: GroupPost) {
    if (!await appConfirm('Delete this post?')) return;
    try {
      await deleteGroupPost(p.id);
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e: any) { alert(e?.message || 'Failed to delete post'); }
  }
  function toggleThread(t: GroupThread) {
    const open = expandedThreadId === t.id ? null : t.id;
    setExpandedThreadId(open);
    if (open) loadReplies(t.id);
  }

  if (!group) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <TopNavigation />
        <LeftNavSidebar />
        <RightWidgetsSidebar />
        <div style={{ marginLeft: 300, marginRight: 340, padding: 20 }}>
          <p>Loading group…</p>
        </div>
      </div>
    );
  }

  const isMemberLocal = isMember;
  
  // Derive posts list based on selected view (non-hook to avoid rules-of-hooks with early return above)
  const derivedPosts = (() => {
    let list = [...posts];
    if (postView === 'active') {
      list.sort((a: any, b: any) => {
        const bTs = b?.lastCommentAt?.toMillis?.() ?? b?.createdAt?.toMillis?.() ?? 0;
        const aTs = a?.lastCommentAt?.toMillis?.() ?? a?.createdAt?.toMillis?.() ?? 0;
        const byTime = bTs - aTs;
        if (byTime !== 0) return byTime;
        return (b?.comments || 0) - (a?.comments || 0);
      });
    } else if (postView === 'mine') {
      const u = auth.currentUser?.uid;
      if (u) list = list.filter((p) => p.userId === u);
    }
    return list;
  })();

  

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--color-background-alt)' }}>
      <TopNavigation />
      {!isMobile && <LeftNavSidebar />}
      {!isMobile && <RightWidgetsSidebar />}
  <div style={{ paddingTop: 16, ...(isMobile ? { marginLeft: 0, marginRight: 0, paddingLeft: 12, paddingRight: 12 } : { marginLeft: 300, marginRight: 340, paddingLeft: 20, paddingRight: 20 }) }}>
        <header style={{ padding: '10px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/groups')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14 }}>← Back</button>
            <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 26 }}>{group.name}</h1>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{group.category} • {group.memberCount || (group.members?.length || 0)} member{(group.memberCount || (group.members?.length || 0)) === 1 ? '' : 's'}</span>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
              {canManage && (
                <button
                  onClick={() => setShowManage((v) => !v)}
                  style={{ padding: '8px 12px', borderRadius: 8, background: showManage ? 'var(--color-primary-50)' : 'transparent', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13 }}
                >⚙️ Manage</button>
              )}
              {isSuper && (
                <button
                  onClick={handleDeleteGroup}
                  style={{ padding: '8px 12px', borderRadius: 8, background: 'transparent', color: '#B91C1C', border: '1px solid #FCA5A5', fontSize: 13 }}
                >🗑 Delete group</button>
              )}
              <button
                onClick={() => navigate(`/groups/${group.id}/members`)}
                style={{ padding: '8px 12px', borderRadius: 8, background: 'transparent', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13 }}
              >👥 Members</button>
              {!isMemberLocal ? (
                <button
                  onClick={async () => {
                    if (!auth.currentUser) return;
                    try {
                      await joinGroup(group.id);
                    } catch (e) {
                      console.error('Join failed', e);
                      alert('Failed to join group');
                    }
                  }}
                  style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--color-primary-600)', color: '#fff', border: 'none', fontSize: 13 }}
                >Join</button>
              ) : (
                <button
                  onClick={async () => {
                    if (!auth.currentUser) return;
                    try {
                      await leaveGroup(group.id);
                    } catch (e) {
                      console.error('Leave failed', e);
                      alert('Failed to leave group');
                    }
                  }}
                  style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--color-border)', color: 'var(--color-text)', border: 'none', fontSize: 13 }}
                >Leave</button>
              )}
            </div>
          </div>
        </header>

        <p style={{ marginTop: 0, color: 'var(--color-text)', fontSize: 14, lineHeight: 1.5 }}>{group.description}</p>

        {showManage && canManage && (
          <GroupManagePanel
            group={group}
            myRole={myRole}
            onUpdated={(patch) => setGroup((g) => (g ? { ...g, ...patch } : g))}
            onClose={() => setShowManage(false)}
          />
        )}

        {/* Top-level tabs: Posts vs Discussions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 12 }}>
          <button onClick={() => setActiveTab('posts')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: activeTab === 'posts' ? 'var(--color-primary-50)' : 'transparent', color: 'var(--color-text)' }}>Posts</button>
          <button onClick={() => setActiveTab('discussions')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: activeTab === 'discussions' ? 'var(--color-primary-50)' : 'transparent', color: 'var(--color-text)' }}>Discussions</button>
        </div>

        {isMemberLocal ? (
          <>
            {activeTab === 'posts' && (
            <section style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <h3 style={{ marginTop: 0 }}>Create a post</h3>
              <textarea value={newPost} onChange={(e) => setNewPost(e.target.value)} rows={3} placeholder="Share something with the group…" style={{ width: '100%', padding: 12, borderRadius: 8, border: '2px solid var(--color-border)', boxSizing: 'border-box' }} />
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={handleCreatePost} disabled={posting || !newPost.trim()} style={{ padding: '10px 16px', borderRadius: 8, background: 'var(--color-primary-600)', color: '#fff', border: 'none' }}>{posting ? 'Posting…' : 'Post'}</button>
              </div>
            </section>
            )}

            {activeTab === 'discussions' && (
            <section style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <h3 style={{ marginTop: 0 }}>Discussions</h3>
              <input type="text" value={newThreadTitle} onChange={(e) => setNewThreadTitle(e.target.value)} placeholder="Thread title" style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid var(--color-border)' }} />
              <textarea value={newThreadBody} onChange={(e) => setNewThreadBody(e.target.value)} placeholder="Start the discussion…" rows={3} style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid var(--color-border)', marginTop: 8 }} />
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={handleCreateThread} disabled={postingThread || !newThreadTitle.trim() || !newThreadBody.trim()} style={{ padding: '10px 16px', borderRadius: 8, background: 'var(--color-primary-600)', color: '#fff', border: 'none' }}>{postingThread ? 'Creating…' : 'Create Thread'}</button>
              </div>

              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[...threads].sort((a, b) => (Number(!!b.pinned) - Number(!!a.pinned))).map((t) => (
                  <div key={t.id} id={`thread-${t.id}`} style={{ border: `2px solid ${highlightThreadId === t.id ? 'var(--color-primary-600)' : 'var(--color-border)'}`, borderRadius: 10, padding: 12 }} className="comment-card fade-in">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <ImageWithFallback src={t.userPhoto} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      <div>
                      {/* Members modal removed in favor of dedicated page */}
                        <UserLink userId={t.userId} as="strong">{t.userName}</UserLink>
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t.createdAt?.toDate?.()?.toLocaleString() || 'Just now'}</div>
                      </div>
                    </div>
                    <h4 style={{ margin: '8px 0 4px 0' }}>
                      {t.title}
                      {t.pinned && <span style={{ marginLeft: 8, fontSize: 12, color: '#AD1457' }}>📌 Pinned</span>}
                      {t.locked && <span style={{ marginLeft: 8, fontSize: 12, color: '#6B7280' }}>🔒 Locked</span>}
                    </h4>
                    <p style={{ marginTop: 0 }}>{t.body}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                      <div style={{ color: 'var(--color-text-muted)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button
                          onClick={() => toggleThread(t)}
                          className="icon-btn"
                          aria-label="Toggle comments"
                        >💬 {t.repliesCount || 0}</button>
                        <button
                          onClick={async () => {
                            if (!auth.currentUser) return;
                            const liked = (t.likes || []).includes(auth.currentUser.uid);
                            try {
                              await toggleGroupThreadLike(t.id);
                              setThreads((prev) => prev.map((th) => {
                                if (th.id !== t.id) return th;
                                const nextLikes = liked
                                  ? (th.likes || []).filter((id) => id !== auth.currentUser!.uid)
                                  : [...(th.likes || []), auth.currentUser!.uid];
                                return { ...th, likes: nextLikes };
                              }));
                            } catch (e) {
                              console.error('Toggle thread like failed', e);
                            }
                          }}
                          className="icon-btn"
                          aria-label="Like thread"
                        >{auth.currentUser && (t.likes || []).includes(auth.currentUser.uid) ? '❤️' : '🤍'} {(t.likes || []).length}</button>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {canManage && (
                          <>
                            <button onClick={() => handleTogglePin(t)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent' }}>{t.pinned ? 'Unpin' : 'Pin'}</button>
                            <button onClick={() => handleToggleLock(t)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent' }}>{t.locked ? 'Unlock' : 'Lock'}</button>
                          </>
                        )}
                        {(canManage || isSuper || t.userId === uid) && (
                          <button onClick={() => handleDeleteThread(t)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', color: '#B91C1C' }}>Delete</button>
                        )}
                        <button onClick={() => toggleThread(t)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent' }}>{expandedThreadId === t.id ? 'Hide' : 'View Thread'}</button>
                      </div>
                    </div>

                    {expandedThreadId === t.id && (
                      <div style={{ marginTop: 8 }}>
                        {(() => {
                          const replies = repliesByThread[t.id] || [];
                          const roots = replies.filter((r) => !r.parentId);
                          const childrenMap = new Map<string, GroupReply[]>();
                          replies.forEach((r) => {
                            if (r.parentId) {
                              if (!childrenMap.has(r.parentId)) childrenMap.set(r.parentId, []);
                              childrenMap.get(r.parentId)!.push(r);
                            }
                          });
                          return roots.map((r) => (
                            <div key={r.id} style={{ padding: 8, borderTop: '1px solid var(--color-border)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ImageWithFallback src={r.userPhoto} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                                <UserLink userId={r.userId} as="strong" style={{ fontSize: 13 }}>{r.userName}</UserLink>
                                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{r.createdAt?.toDate?.()?.toLocaleString() || ''}</span>
                              </div>
                              <div style={{ marginLeft: 36, marginTop: 6 }}>
                                <div style={{ marginBottom: 6 }}>{r.body}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-muted)' }}>
                                  <button
                                    onClick={async () => {
                                      if (!auth.currentUser) return;
                                      const liked = (r.likes || []).includes(auth.currentUser.uid);
                                      try {
                                        setRepliesByThread((prev) => {
                                          const next = { ...prev };
                                          const items = next[t.id] || [];
                                          next[t.id] = items.map((reply) => {
                                            if (reply.id !== r.id) return reply;
                                            const nextLikes = liked
                                              ? (reply.likes || []).filter((id) => id !== auth.currentUser!.uid)
                                              : [...(reply.likes || []), auth.currentUser!.uid];
                                            return { ...reply, likes: nextLikes };
                                          });
                                          return next;
                                        });
                                      } catch (e) {
                                        console.error('Toggle reply like failed', e);
                                      }
                                    }}
                                    style={{ background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}
                                  >{auth.currentUser && (r.likes || []).includes(auth.currentUser.uid) ? '❤️' : '🤍'} {(r.likes || []).length}</button>
                                  <button onClick={() => setReplyingToReply((m) => ({ ...m, [r.id]: !m[r.id] }))} style={{ background: 'transparent', border: 'none', fontSize: 12, cursor: 'pointer', color: 'inherit' }}>Reply</button>
                                </div>

                                {replyingToReply[r.id] && (
                                  <div style={{ marginTop: 6 }}>
                                    <textarea
                                      value={childReplyText[r.id] || ''}
                                      onChange={(e) => setChildReplyText((m) => ({ ...m, [r.id]: e.target.value }))}
                                      rows={2}
                                      placeholder={t.locked ? 'Thread is locked' : 'Write a reply…'}
                                      style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--color-border)' }}
                                      disabled={!!t.locked}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                                      <button onClick={() => handlePostChildReply(r, t)} disabled={!!t.locked || loadingReplies[t.id] || !(childReplyText[r.id] || '').trim()} style={{ padding: '6px 10px', borderRadius: 6, background: 'var(--color-primary-600)', color: '#fff', border: 'none' }}>{loadingReplies[t.id] ? 'Posting…' : 'Reply'}</button>
                                    </div>
                                  </div>
                                )}

                                {(childrenMap.get(r.id) || []).map((cr) => (
                                  <div key={cr.id} style={{ marginTop: 8, marginLeft: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <ImageWithFallback src={cr.userPhoto} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                                      <UserLink userId={cr.userId} as="strong" style={{ fontSize: 12 }}>{cr.userName}</UserLink>
                                      <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{cr.createdAt?.toDate?.()?.toLocaleString() || ''}</span>
                                    </div>
                                    <div style={{ marginLeft: 32, marginTop: 4 }}>
                                      <div style={{ marginBottom: 4 }}>{cr.body}</div>
                                      <button
                                        onClick={async () => {
                                          if (!auth.currentUser) return;
                                          const liked = (cr.likes || []).includes(auth.currentUser.uid);
                                          try {
                                            setRepliesByThread((prev) => {
                                              const next = { ...prev };
                                              const items = next[t.id] || [];
                                              next[t.id] = items.map((reply) => {
                                                if (reply.id !== cr.id) return reply;
                                                const nextLikes = liked
                                                  ? (reply.likes || []).filter((id) => id !== auth.currentUser!.uid)
                                                  : [...(reply.likes || []), auth.currentUser!.uid];
                                                return { ...reply, likes: nextLikes };
                                              });
                                              return next;
                                            });
                                          } catch (e) {
                                            console.error('Toggle child reply like failed', e);
                                          }
                                        }}
                                        style={{ background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}
                                      >{auth.currentUser && (cr.likes || []).includes(auth.currentUser.uid) ? '❤️' : '🤍'} {(cr.likes || []).length}</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ));
                        })()}
                        <div style={{ marginTop: 8 }}>
                          <textarea value={replyText[t.id] || ''} onChange={(e) => setReplyText((m) => ({ ...m, [t.id]: e.target.value }))} placeholder={t.locked ? 'Thread is locked' : 'Write a reply…'} rows={2} style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid var(--color-border)', opacity: t.locked ? 0.6 : 1 }} disabled={!!t.locked} />
                          <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={() => handlePostReply(t)} disabled={!!t.locked || loadingReplies[t.id] || !(replyText[t.id] || '').trim()} style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--color-primary-600)', color: '#fff', border: 'none' }}>{loadingReplies[t.id] ? 'Posting…' : (t.locked ? 'Locked' : 'Reply')}</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
            )}

            {activeTab === 'posts' && (
              <section>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <h3 style={{ margin: 0 }}>Group Posts</h3>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button onClick={() => setPostView('new')} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: postView==='new' ? 'var(--color-primary-50)' : 'transparent' }}>New</button>
                    <button onClick={() => setPostView('active')} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: postView==='active' ? 'var(--color-primary-50)' : 'transparent' }}>Active</button>
                    <button onClick={() => setPostView('mine')} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: postView==='mine' ? 'var(--color-primary-50)' : 'transparent' }}>My posts</button>
                  </div>
                </div>
                {derivedPosts.length === 0 ? (
                  <p style={{ color: 'var(--color-text-muted)' }}>No posts yet. Be the first to share!</p>
                ) : (
                  derivedPosts.map((post, postIdx) => (
                    <React.Fragment key={post.id}>
                    {postIdx > 0 && postIdx % 6 === 0 && <div style={{ marginBottom: 12 }}><AdSlot placement="feed" /></div>}
                    <div style={{ background: post.userId === uid ? 'rgba(23,162,169,0.06)' : 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 12, marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <ImageWithFallback src={post.userPhoto} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                        <div>
                          <strong><UserLink userId={post.userId}>{post.userName}</UserLink> {post.userId === uid && <span style={{ marginLeft: 6, color: '#0B6B6F', fontSize: 12 }}>(You)</span>}</strong>
                          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{post.createdAt?.toDate?.()?.toLocaleString() || 'Just now'}</div>
                        </div>
                      </div>
                      <p style={{ marginTop: 8 }}>{post.content}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                        <div style={{ display: 'flex', gap: 12, color: 'var(--color-text-muted)', fontSize: 13 }}>
                          <button
                            onClick={async () => {
                              if (!auth.currentUser) return;
                              const liked = (post.likes || []).includes(auth.currentUser.uid);
                              try {
                                await toggleGroupPostLike(post.id);
                                setPosts((prev) => prev.map((p) => {
                                  if (p.id !== post.id) return p;
                                  const nextLikes = liked
                                    ? (p.likes || []).filter((id) => id !== auth.currentUser!.uid)
                                    : [...(p.likes || []), auth.currentUser!.uid];
                                  return { ...p, likes: nextLikes };
                                }));
                              } catch (e) {
                                console.error('Toggle like failed', e);
                              }
                            }}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: '6px 8px', borderRadius: 6 }}
                            aria-label="Like post"
                          >{(auth.currentUser && (post.likes || []).includes(auth.currentUser.uid)) ? '❤️' : '🤍'} {(post.likes || []).length}</button>
                          <button
                            onClick={() => setOpenCommentsByPost((m) => ({ ...m, [post.id]: !m[post.id] }))}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: '6px 8px', borderRadius: 6 }}
                            aria-label="Toggle comments"
                          >💬 {post.comments || 0}</button>
                        </div>
                        {(canManage || isSuper || post.userId === uid) && (
                          <button onClick={() => handleDeletePost(post)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#B91C1C', padding: '6px 8px', borderRadius: 6, fontSize: 13 }} aria-label="Delete post">🗑 Delete</button>
                        )}
                      </div>
                      {openCommentsByPost[post.id] && (
                        <GroupComments contentId={post.id} contentOwnerId={post.userId} />
                      )}
                    </div>
                    </React.Fragment>
                  ))
                )}
              </section>
            )}
          </>
        ) : (
          <p style={{ color: 'var(--color-text-muted)' }}>Join this group to see posts and participate in discussions.</p>
        )}
      </div>
    </div>
  );
};

export default GroupDetail;
